<template>
  <v-app-bar
    app
    flat
    :height="headerHeight"
    :class="clearColor"
  >
    <v-row>
      <v-col class="flex-grow">
        <v-row class="d-flex flex-nowrap mr-2">
          <v-col
            class="col-grow pl-2"
          >
            <search-bar
              v-if="showSearchBar"
              class="client-search font-weight-medium"
              menu-class="header-bar-search"
            />
          </v-col>

          <v-col
            v-if="showDataFreshness && dataFreshnessIndicators"
            class="col-auto pl-4"
          >
            <v-dialog
              v-model="toggleDataFreshnessLegend"
              hide-overlay
              max-width="600"
            >
              <template v-slot:activator="{ on }">
                <v-row
                  class="cursor-pointer pt-3 align-center caption flex-nowrap"
                  v-on="on"
                >
                  <v-col class="col-1 px-0 ml-1 mr-2 data-refresh-icon">
                    <v-icon
                      class="mr-1"
                      :color="dataFreshnessIndicators.color"
                      medium
                    >
                      {{ dataFreshnessIndicators.icon }}
                    </v-icon>
                  </v-col>

                  <v-col
                    v-if="showDataFreshnessDetails"
                    class="col-10 ml-2"
                  >
                    <v-row
                      :class="dataFreshnessSpansTwoLinesOfText ? 'font-weight-bold' : 'font-weight-medium line-height-double'"
                    >
                      <v-col class="pa-0 text-capitalize text-truncate data-refresh-text">
                        {{ dataFreshnessIndicators.text }}
                      </v-col>
                    </v-row>

                    <v-row
                      v-if="dataFreshnessSpansTwoLinesOfText"
                      class="mt-1 text-capitalize"
                    >
                      <v-col class="pa-0 text-truncate data-refresh-text">
                        {{ dataFreshnessIndicators.subtext }}

                        <span class="font-weight-medium">
                          {{ dataFreshnessIndicators.datetime }}
                        </span>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </template>

              <v-card>
                <v-card-title class="title px-9 py-6">
                  <v-row>
                    <v-col
                      class="flex-grow py-0 darkestblue--text"
                    >
                      Data Status
                    </v-col>

                    <v-spacer />

                    <v-col
                      class="flex-shrink-1 col-2 py-0 text-right"
                    >
                      <v-btn
                        :ripple="false"
                        icon
                        @click.stop="hideLegendDataFreshness"
                      >
                        <v-icon color="brightblue">
                          close
                        </v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-title>

                <v-divider />

                <v-card-text class="px-6 pb-6">
                  <v-row
                    class="mt-1 text-capitalize"
                  >
                    <v-col class="ma-6">
                      <v-row>
                        <v-col>
                          <v-row class="pr-1 font-weight-bold">
                            Status
                          </v-row>

                          <v-row class="mt-2 font-weight-medium">
                            {{ dataFreshnessIndicators.text }}
                          </v-row>
                        </v-col>
                      </v-row>

                      <v-row
                        v-if="dataFreshnessIndicators.subtext"
                        class="pt-3"
                      >
                        <v-col>
                          <v-row class="pr-1 font-weight-bold">
                            <span>Details</span>
                          </v-row>

                          <v-row class="mt-2 font-weight-medium">
                            {{ dataFreshnessIndicators.subtext }}
                          </v-row>
                        </v-col>
                      </v-row>

                      <v-row
                        v-if="dataFreshnessIndicators.datetime"
                        class="pt-3"
                      >
                        <v-col>
                          <v-row class="pr-1 font-weight-bold">
                            <span>Last Updated</span>
                          </v-row>

                          <v-row class="mt-2 font-weight-medium">
                            {{ dataFreshnessIndicators.datetime }}
                          </v-row>
                        </v-col>
                      </v-row>

                      <v-row
                        v-if="dataFreshnessIndicators.datetime"
                        class="pt-3"
                      >
                        <v-col>
                          <v-row class="pr-1 font-weight-bold">
                            Timezone
                          </v-row>

                          <v-row class="mt-2 font-weight-medium">
                            Local Time
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-dialog>
          </v-col>
        </v-row>
      </v-col>

      <v-col
        class="col-auto d-flex"
      >
        <v-row class="d-flex flex-nowrap justify-end justify-xl-end justify-lg-end justify-md-end justify-sm-end justify-xs-center align-center pt-1">
          <v-col
            v-if="showFilterBar"
            class="header-action pl-sm-0 pl-md-8 pl-lg-8 pl-xl-8 pl-8 ml-3 mt-1 text-right text-sm-end text-xs-center font-weight-medium"
            style="border-left: 1px solid #F3F3F4FF"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-row
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-col style="margin-top: 2px; max-width: 195px;">
                    <v-autocomplete
                      v-model="syncItemsPerPage"
                      autocomplete="off"
                      class="caption font-weight-medium"
                      dense
                      :items="itemsPerPageOptions"
                      :label="rowsPerPageLabel"
                      hide-details
                      name="rowsPerPage"
                    />
                  </v-col>
                </v-row>
              </template>

              <div class="caption">
                <b>Rows Per Page</b>
                <br>
                {{ rowsPerPageLabel }}
              </div>
            </v-tooltip>
          </v-col>

          <v-col
            v-if="showFilterBar"
            class="header-action pl-sm-0 pr-0 pt-1 mr-3 mt-1 text-center"
            style="border-right: 1px solid #F3F3F4FF;"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-row
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-col
                    class="text-center py-1"
                    style="max-width: 120px;"
                  >
                    <div
                      class="caption font-weight-medium coolgray--text"
                      style="line-height: 21px;"
                    >
                      Page {{ syncPage }} of {{ syncPageCount }}
                    </div>

                    <v-pagination
                      v-model="syncPage"
                      circle
                      :length="syncPageCount"
                      next-icon="navigate_next"
                      prev-icon="navigate_before"
                      :total-visible="0"
                      dense
                      x-small
                    />
                  </v-col>
                </v-row>
              </template>

              <div class="caption">
                <b>Paginate Results</b>
                <br>
                Page {{ syncPage }} of {{ syncPageCount }}
              </div>
            </v-tooltip>
          </v-col>

          <v-col
            :class="showItemBar ? 'header-action col-1 pl-sm-0 text-right text-sm-end  text-xs-center' : 'd-none'"
          >
            <v-badge
              v-if="showItemBar"
              value="showItemBar"
              avatar
              overlap
              color="primary"
              class="mr-4 white--text"
              small
            >
              <template v-slot:badge>
                <v-icon
                  style="margin-top: 2px"
                  color="white"
                  small
                >
                  add
                </v-icon>
              </template>

              <v-tooltip bottom>
                <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                  <v-btn
                    small
                    text
                    class="white--text create-item"
                    color="primary"
                    depressed
                    v-bind="tooltipAttrs"
                    v-on="tooltipOn"
                    @click.stop="createItem(undefined)"
                  >
                    <img
                      :src="addIcon"
                      width="21"
                    >
                  </v-btn>
                </template>

                <span>Add</span>
              </v-tooltip>
            </v-badge>
          </v-col>

          <v-col
            v-if="showLegendBar"
            class="header-action col-1 pl-sm-0 text-right text-sm-end  text-xs-center"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                <v-btn
                  color="coolgray"
                  icon
                  small
                  v-bind="tooltipAttrs"
                  v-on="tooltipOn"
                  @click="toggleLegend"
                >
                  <v-icon>
                    info
                  </v-icon>
                </v-btn>
              </template>

              <span>Legend</span>
            </v-tooltip>

            <legend-bar />
          </v-col>

          <v-col
            v-if="showExportBar"
            class="header-action col-1 pl-sm-0 text-right text-sm-end  text-xs-center"
          >
            <v-menu
              :disabled="!showExportBar"
              min-width="90"
              offset-y
              nudge-bottom="6"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                    <v-btn
                      :color="showExportBar ? 'coolgray' : 'vuetifygray'"
                      dark
                      icon
                      small
                      title="Export"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon
                        v-bind="tooltipAttrs"
                        v-on="tooltipOn"
                      >
                        system_update
                      </v-icon>
                    </v-btn>
                  </template>

                  <span>Export</span>
                </v-tooltip>
              </template>

              <v-list>
                <v-list-item
                  v-for="item in exportOptions"
                  :key="item.key"
                  :disabled="!item.active"
                  @click="item.callback"
                >
                  <v-list-item-title>
                    <v-col>
                      <v-row v-if="item.key !== 'xlsx'">
                        {{ item.text }}
                      </v-row>

                      <v-row v-else>
                        <export-xlsx
                          :item="item"
                          :show-export-bar="showExportBar"
                        />
                      </v-row>
                    </v-col>
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>

          <v-col
            v-if="showLayoutBar"
            class="header-action col-1 pl-sm-0 text-right text-sm-end  text-xs-center"
          >
            <v-menu
              :disabled="!showLayoutBar"
              offset-y
              nudge-bottom="6"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                    <v-btn
                      :color="showLayoutBar ? 'coolgray' : 'vuetifygray'"
                      dark
                      icon
                      small
                      title="Layout"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon
                        v-bind="tooltipAttrs"
                        v-on="tooltipOn"
                      >
                        expand
                      </v-icon>
                    </v-btn>
                  </template>

                  <span>Layouts</span>
                </v-tooltip>
              </template>

              <v-list>
                <v-list-item
                  v-for="item in layouts"
                  :key="item.key"
                  dense
                  :disabled="!item.active"
                  :style="`${ item.header ? 'min-height: 21px !important;' : '' }`"
                  :title="item.helpText"
                  @click="item.callback"
                >
                  <v-list-item-title>
                    <v-col
                      v-if="item.header"
                      class="caption pb-0 pt-2"
                    >
                      {{ item.header }}
                    </v-col>

                    <v-col v-else>
                      {{ item.text }}
                    </v-col>
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>

          <v-col
            class="header-action col-1 pl-sm-0 text-right text-sm-end  text-xs-center"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="coolgray"
                  icon
                  small
                  target="_blank"
                  title="Release Notes"
                  to="/docs/releases"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>
                    notifications_none
                  </v-icon>
                </v-btn>
              </template>

              <span>Release Notes</span>
            </v-tooltip>
          </v-col>

          <v-col
            class="header-action col-1 pl-sm-0 text-right text-sm-end  text-xs-center"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="coolgray"
                  icon
                  small
                  target="_blank"
                  title="Feedback"
                  href="https://forms.gle/L3rmX38g7DYodqPt6"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>
                    comment
                  </v-icon>
                </v-btn>
              </template>

              <span>Feedback</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-app-bar>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import format from 'src/utils/format'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapActions: mapInternalActions } = createNamespacedHelpers('internal')
const { mapActions: mapLayoutActions, mapGetters: mapLayoutGetters, mapState: mapLayoutState } = createNamespacedHelpers('layout')
const { mapActions: mapToolsActions, mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

export default {
  name: 'Header',
  components: {
    ExportXlsx: () => import('@/tools/components/export/ExportXlsx'),
    LegendBar: () => import('src/delivery/components/modals/LegendBar'),
    // FilterBar: () => import('src/tools/components/filter/FilterBar'),
    SearchBar: () => import('src/tools/components/search/SearchBar'),
  },
  computed: {
    ...mapAuthGetters(['inDemoMode', 'isInternalWriter', 'permissions']),
    ...mapToolsGetters([
      'currentColumns',
      'currentGroups',
      'currentRowCount',
      'indicateDataRefreshed',
      'indicateDataRefreshing',
    ]),
    ...mapLayoutGetters([
      'clearColor',
      'showDataFreshness',
      'showExportBar',
      'showFilterBar',
      'showLayoutBar',
      'showLegendBar',
      'showSearchBar',
    ]),
    ...mapToolsState({
      internalRoutes: (state) => state.internalRoutes,
      isDataFresh: (state) => state.isDataFresh,
      isLegendModalVisible: (state) => state.isLegendModalVisible,
      itemsPerPage: (state) => state.itemsPerPage,
      itemsPerPageOptions: (state) => state.itemsPerPageOptions,
      page: (state) => state.page,
      pageCount: (state) => state.pageCount,
      baseReportName: (state) => state.baseReportName,
      showDataFreshnessLegend: (state) => state.showDataFreshnessLegend,
    }),
    ...mapLayoutState({
      icons: (state) => state.icons,
      isNavCollapsed: (state) => state.isNavCollapsed,
      $isLayoutFullScreen: (state) => state.isLayoutFullScreen,
    }),

    isLayoutFullScreen() {
      return this.$isLayoutFullScreen
    },
    showDataFreshnessDetails() {
      return this.isNavCollapsed ? this.$vuetify.breakpoint.width > 1150 : this.$vuetify.breakpoint.width > 1315
    },
    headerHeight() {
      const showFilterBarOnNarrowScreen = (this.showFilterBar && this.$vuetify.breakpoint.width < 857)
        || (!this.isNavCollapsed && this.$vuetify.breakpoint.width < 1076)
      return (showFilterBarOnNarrowScreen && '128') || '64'
    },

    syncPage: {
      get() {
        return this.page
      },
      set(value) {
        this.$store.commit('tools/page', value)
      },
    },
    syncPageCount: {
      get() {
        return this.pageCount || 1
      },
      set(value) {
        this.$store.commit('tools/pageCount', value)
      },
    },
    syncItemsPerPage: {
      get() {
        return this.itemsPerPage
      },
      set(value) {
        this.$store.commit('tools/itemsPerPage', value)
      },
    },
    currentRowsIndexLast() {
      const indexLastPossible = this.currentRowCount && (this.syncItemsPerPage * this.syncPage)
      return Math.min(indexLastPossible, this.currentRowCount)
    },
    currentRowsIndexFirst() {
      const remainder = this.syncPage === this.syncPageCount && this.currentRowCount % this.syncItemsPerPage
      const calculateIndexOfFirstRow = () => {
        const firstRowIndex = this.currentRowsIndexLast - (remainder !== false ? remainder : this.syncItemsPerPage)
        return firstRowIndex < 0 ? 1 : firstRowIndex + 1
      }
      const getRowCount = () => (this.currentRowCount > 0 ? 1 : 0)
      return this.pageCount === 0 ? getRowCount() : (this.currentRowCount && calculateIndexOfFirstRow()) || 0
    },
    rowsPerPageLabel() {
      const currentRowCount = this.currentRowCount && format.number(this.currentRowCount)
      return `Rows ${this.currentRowsIndexFirst} - ${this.currentRowsIndexLast} of ${currentRowCount}`
    },

    toggleDataFreshnessLegend: {
      get() {
        return this.showDataFreshnessLegend
      },
      set(value) {
        this.$store.commit('tools/showDataFreshnessLegend', value)
      },
    },
    addIcon() {
      const { icons: { navigation: { add: addIcons } }, baseReportName } = this

      return (!!baseReportName && addIcons[baseReportName]) || null
    },
    showItemBar() {
      const { addIcon, internalRoutes, isInternalWriter, baseReportName } = this
      const isAddRoute = () => addIcon && internalRoutes.includes(baseReportName)

      return baseReportName && isInternalWriter && isAddRoute()
    },
    /**
     * Gets the view data for the data freshness indicator
     *
     * @returns {Object} indicator { color, datetime, icon, subtext, text }
     */
    dataFreshnessIndicators() {
      const {
        indicateDataRefreshed,
        indicateDataRefreshing,
      } = this

      if (indicateDataRefreshing) return indicateDataRefreshing

      if (indicateDataRefreshed) return indicateDataRefreshed

      return false
    },
    dataFreshnessSpansTwoLinesOfText() {
      const { dataFreshnessIndicators } = this
      return dataFreshnessIndicators.subtext || dataFreshnessIndicators.datetime
    },
    layouts() {
      const { currentGroups, isLayoutFullScreen, showFilterBar, showLayoutBar } = this
      const haveGroupedData = () => !!currentGroups?.length && currentGroups[0].isPartOfGroup
      const includeLayoutForGrouping = showFilterBar && haveGroupedData()

      const layouts = [
        {
          active: false,
          callback() {
            // Log to Google Analytics
          },
          header: 'Layout Modes',
        },
        {
          active: showLayoutBar,
          helpText: 'Collapses the view to the essential elements, allowing you to focus on the data.',
          key: 'focus',
          text: `${isLayoutFullScreen ? 'Exit' : 'Enter'} Focus Mode`,
          callback: this.toggleFullScreen,
        },
      ]

      if (includeLayoutForGrouping) {
        const headerRowGroupBy = {
          active: false,
          callback() {
            // Log to Google Analytics
          },
          header: 'Manage Table Groups',
        }
        const toggleGroups = {
          active: includeLayoutForGrouping,
          helpText: 'Toggle the open/close state of all table groups.',
          key: 'groups',
          text: 'Toggle Groups',
          callback: this.toggleGroups,
        }

        layouts.push(headerRowGroupBy, toggleGroups)
      }

      return layouts
    },

    exportOptions() {
      const { showExportBar } = this

      return [
        {
          active: showExportBar,
          key: 'csv',
          text: 'CSV',
          callback: this.exportAsCsv,
        },
        {
          active: showExportBar,
          key: 'xlsx',
          text: 'XLSX',
          callback: this.exportAsXlsx,
        },
      ]
    },
  },
  watch: {
    inDemoMode() {
      this.$store.dispatch('tools/getDataRefresh')
    },
  },
  created() {
    this.$store.dispatch('tools/getDataRefresh')
  },
  methods: {
    ...mapLayoutActions(['toggleFullScreen']),
    ...mapInternalActions(['createItem']),
    ...mapToolsActions(['exportAs', 'toggleGroups']),
    exportAsCsv() {
      const {
        currentColumns: exportColumns,
        currentGroups: exportGroups,
        baseReportName: exportName,
      } = this
      const exportFormat = 'csv'

      return this.exportAs({ exportColumns, exportFormat, exportGroups, exportName })
    },
    exportAsXlsx() {
      const {
        currentColumns: exportColumns,
        currentGroups: exportGroups,
        baseReportName: exportName,
      } = this
      const exportFormat = 'xlsx'

      return this.exportAs({ exportColumns, exportFormat, exportGroups, exportName })
    },
    toggleLegend() {
      this.$store.commit('delivery/isLegendModalVisible', !this.isLegendModalVisible)
    },
    hideLegendDataFreshness() {
      this.toggleDataFreshnessLegend = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

.client-search {
  min-width: 120px;
}

.header-action {
  min-width: 52px;
}

.data-refresh-icon {
  max-width: 16px;
}

.data-refresh-text {
  min-width: fit-content;
}

.v-select.v-text-field input {
  max-width: 0 !important;
}

.create-item {
  min-width: 21px !important;
  padding: 0 !important;
}

.line-height-double {
  line-height: 2;
}

</style>
